import Phaser from "phaser";
import { getAgritechText } from "../utils/agritechText";

export default class OptionsScene extends Phaser.Scene {
  constructor() {
    super("OptionsScene");
  }

  create() {
    const getSetting = (key, fallback) => localStorage.getItem(key) ?? fallback;
    const setSetting = (key, value) => localStorage.setItem(key, value);

    let language = getSetting("agritech-language", "es");
    let musicVolume = Number(getSetting("agritech-music-volume", "0.4"));
    let effectsVolume = Number(getSetting("agritech-effects-volume", "0.6"));
    const t = getAgritechText();

    const applyGlobalVolumes = () => {
      const musicVolume = Number(
        localStorage.getItem("agritech-music-volume") ?? "0.4",
      );

      const titleScene = this.scene.get("TitleScene");
      const mainScene = this.scene.get("MainScene");
      const resultScene = this.scene.get("ResultScene");

      if (titleScene?.titleMusic) {
        titleScene.titleMusic.setVolume(musicVolume);
      }

      if (mainScene?.levelMusic) {
        mainScene.levelMusic.setVolume(musicVolume);
      }

      if (resultScene?.resultsMusic) {
        resultScene.resultsMusic.setVolume(musicVolume);
      }
    };

    const playTap = () => {
      applyGlobalVolumes();
      this.sound.play("tap", { volume: effectsVolume });
    };

    const playSelected = () => {
      applyGlobalVolumes();
      this.sound.play("selected", { volume: effectsVolume });
    };

    this.add.image(400, 300, "titleBG").setDisplaySize(800, 600);
    this.add.rectangle(400, 300, 800, 600, 0x06130c, 0.62).setDepth(1);

    this.add
      .rectangle(400, 300, 560, 390, 0x18231d, 0.97)
      .setStrokeStyle(3, 0x769b55)
      .setDepth(2);

    this.add
      .text(400, 135, t.optionsTitle, {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "40px",
        color: "#ecfccb",
        stroke: "#14532d",
        strokeThickness: 3,
        shadow: {
          offsetX: 0,
          offsetY: 3,
          color: "#020d07",
          blur: 6,
          fill: true,
        },
      })
      .setOrigin(0.5)
      .setDepth(3);

    // ---------- LANGUAGE SELECTOR ----------

    this.add
      .text(400, 188, "IDIOMA / LANGUAGE\n", {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "15px",
        color: "#cbd5c0",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(4);

    const languageSelector = this.add
      .rectangle(400, 220, 320, 48, 0x0b1510, 1)
      .setStrokeStyle(2, 0x6f8f5b)
      .setDepth(3);

    const selectLanguage = (nextLanguage) => {
      if (nextLanguage === language) return;

      playSelected();

      language = nextLanguage;
      setSetting("agritech-language", language);
      this.registry.set("language", language);

      window.dispatchEvent(new Event("agritech-language-change"));

      // Rebuild TitleScene so all of its text changes language too.
      const titleScene = this.scene.get("TitleScene");

      if (titleScene?.titleMusic) {
        titleScene.titleMusic.stop();
      }

      this.scene.stop("OptionsScene");
      this.scene.stop("TitleScene");
      this.scene.start("TitleScene");
      this.scene.launch("OptionsScene");
    };

    const createLanguageOption = (x, label, value) => {
      const selected = language === value;

      const button = this.add
        .rectangle(x, 220, 152, 38, selected ? 0x65a30d : 0x18231d, 1)
        .setStrokeStyle(2, selected ? 0xd9f99d : 0x405747)
        .setDepth(4)
        .setInteractive({ useHandCursor: true });

      const text = this.add
        .text(x, 220, label, {
          fontFamily: '"Montserrat", sans-serif',
          fontSize: "17px",
          color: selected ? "#ffffff" : "#cbd5c0",
          fontStyle: "bold",
        })
        .setOrigin(0.5)
        .setDepth(5);

      if (selected) {
        this.add.circle(x - 55, 220, 4, 0xd9f99d, 1).setDepth(6);
      }

      button.on("pointerover", () => {
        if (!selected) {
          playTap();
          button.setFillStyle(0x26382b, 1);
          button.setStrokeStyle(2, 0x769b55);
          text.setColor("#d9f99d");
        }
      });

      button.on("pointerout", () => {
        if (!selected) {
          button.setFillStyle(0x18231d, 1);
          button.setStrokeStyle(2, 0x405747);
          text.setColor("#cbd5c0");
        }
      });

      button.on("pointerdown", () => {
        selectLanguage(value);
      });
    };

    createLanguageOption(322, "ESPAÑOL", "es");
    createLanguageOption(478, "ENGLISH", "en");

    const makeSlider = (y, label, value, onChange) => {
      const labelText = this.add
        .text(400, y - 28, `${label}: ${Math.round(value * 100)}%`, {
          fontFamily: '"Montserrat", sans-serif',
          fontSize: "20px",
          color: "#f7faf4",
        })
        .setOrigin(0.5)
        .setDepth(4);

      const trackX = 400;
      const trackWidth = 300;
      const trackLeft = trackX - trackWidth / 2;
      const trackRight = trackX + trackWidth / 2;

      this.add.rectangle(trackX, y, trackWidth, 8, 0x405747, 1).setDepth(3);

      const fill = this.add
        .rectangle(trackLeft, y, trackWidth * value, 8, 0xfacc15, 1)
        .setOrigin(0, 0.5)
        .setDepth(4);

      const handle = this.add
        .circle(trackLeft + trackWidth * value, y, 13, 0xa3e635, 1)
        .setStrokeStyle(3, 0xecfccb)
        .setDepth(7)
        .setInteractive({ useHandCursor: true });

      this.input.setDraggable(handle);

      const updateValueFromX = (x) => {
        const clampedX = Phaser.Math.Clamp(x, trackLeft, trackRight);
        const nextValue = Phaser.Math.Clamp(
          (clampedX - trackLeft) / trackWidth,
          0,
          1,
        );

        handle.x = trackLeft + trackWidth * nextValue;
        fill.width = trackWidth * nextValue;
        labelText.setText(`${label}: ${Math.round(nextValue * 100)}%`);
        onChange(nextValue);
      };

      handle.on("pointerover", () => playTap());
      this.input.on("drag", (pointer, gameObject, dragX) => {
        if (gameObject === handle) {
          updateValueFromX(dragX);
        }
      });

      handle.on("dragend", () => {
        playSelected();
      });

      const clickZone = this.add
        .rectangle(trackX, y, trackWidth + 32, 34, 0xffffff, 0)
        .setDepth(6)
        .setInteractive({ useHandCursor: true });

      handle.setDepth(8);

      clickZone.on("pointerover", () => playTap());
      clickZone.on("pointerdown", (pointer) => {
        playSelected();
        updateValueFromX(pointer.x);
      });
    };

    makeSlider(300, t.musicVolume, musicVolume, (value) => {
      musicVolume = value;
      setSetting("agritech-music-volume", value.toFixed(2));

      applyGlobalVolumes();
    });

    makeSlider(370, t.effectsVolume, effectsVolume, (value) => {
      effectsVolume = value;
      setSetting("agritech-effects-volume", value.toFixed(2));
      applyGlobalVolumes();
    });

    const backButton = this.add
      .rectangle(400, 445, 220, 46, 0x0b1510, 0.95)
      .setStrokeStyle(2, 0x6f8f5b)
      .setDepth(3)
      .setInteractive({ useHandCursor: true });

    const backText = this.add
      .text(400, 445, t.return, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "22px",
        color: "#f8fafc",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(4);

    backButton.on("pointerover", () => {
      playTap();
      backButton.setFillStyle(0x18291f, 1);
      backText.setColor("#d9f99d");
    });

    backButton.on("pointerout", () => {
      backButton.setFillStyle(0x0b1510, 0.95);
      backText.setColor("#f8fafc");
    });

    backButton.on("pointerdown", () => {
      playSelected();
      this.scene.stop("OptionsScene");
      this.scene.resume("TitleScene");
    });
  }
}
